import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useOllamaInstalledModels, useProviderCatalog } from '../queries';
import type {
  ProviderCatalogEntry,
  ProviderCatalogModel,
  ProvidersCatalogResponse,
} from '../services/providerService';
import { ollamaModelOptions, type ModelOption } from '../labs/shared/agent-bar/modelOptions';
import { describeModelPrice, formatModelPrice } from './modelPricing';
import {
  filteredSortedModels,
  groupLabelForModel,
} from './modelSortFilter';

export type ProviderKind = 'llm' | 'image';

interface Props {
  kind: ProviderKind;
  provider: string;
  model: string;
  onProviderChange: (next: string) => void;
  onModelChange: (next: string) => void;
  /** Inline (single-row, used in tight headers like AgentBar) vs stacked (default
   *  for forms — provider on top, model below). */
  variant?: 'inline' | 'stacked';
  size?: 'small' | 'medium';
  /** Disable both fields (e.g. while a parent operation is in flight). */
  disabled?: boolean;
  /** Hide the provider selector when there's only one viable provider — keeps
   *  the surface terse if only one provider is wired. */
  hideProviderWhenSingle?: boolean;
  /** Hide the "Hide legacy" toggle (compact inline contexts). When omitted
   *  the toggle defaults to ON in stacked layouts and is HIDDEN inline. */
  showLegacyToggle?: boolean;
  /**
   * Filter the model list to "image-aware" models — those whose tags include
   * any of i2i / edit / inpaint / outpaint / upscale / face / tryon. Set this
   * when the active op consumes a source image, so the user can't pick a
   * text-to-image-only model (the case behind the 2026-05-26 BE enforcement
   * fix at `ssgp-v3-agents-simple/Services/Images/ModelCapabilityChecker.cs`).
   * Defaults false — image-aware AND text-to-image models both appear.
   */
  requiresImageAware?: boolean;
  /**
   * Inverse of {@link requiresImageAware}: filter the list to models that can
   * generate from a text prompt with NO source image. Set on the TextToImage
   * (Generate) panel so the user can't pick an edit/inpaint/upscale model
   * (e.g. `fal-ai/gpt-image-2/edit`) that requires `image_urls` — which fails
   * upstream with "Number of image URLs must be at least 1".
   */
  textToImageOnly?: boolean;
  /**
   * Only meaningful when `kind="llm"`. When true, the image providers
   * (fal.ai + its models, Gemini Image, Luma) are appended to the provider
   * list under a separate "Image generation" group, so the agent bar can
   * offer image generation alongside chat models. The caller is responsible
   * for routing a selected image provider to the image-generation path —
   * see `isImageProviderKey`.
   */
  includeImageProviders?: boolean;
}

/**
 * Shared provider + model picker. Reads `/api/v3/providers/catalog` so the
 * options list mirrors what the BE actually has wired — no FE-side hardcoded
 * provider lists.
 *
 * Special-cases LLM Ollama: fuses the catalog list with the daemon's live
 * `/api/tags` + curated cloud-tag passthroughs via the existing
 * `ollamaModelOptions` helper, so users see installed-local + Ollama-Cloud
 * + Suggested + free-text Custom options in one Autocomplete.
 *
 * Image providers get a plain Autocomplete (freeSolo so power users can paste
 * a fal model id the catalog hasn't surfaced yet).
 */
export const ProviderModelSelect: React.FC<Props> = ({
  kind,
  provider,
  model,
  onProviderChange,
  onModelChange,
  variant = 'stacked',
  size = 'small',
  disabled = false,
  hideProviderWhenSingle = false,
  showLegacyToggle,
  requiresImageAware = false,
  textToImageOnly = false,
  includeImageProviders = false,
}) => {
  const catalog = useProviderCatalog();
  const ollamaInstalled = useOllamaInstalledModels();
  // Default: hide legacy. Stacked layouts surface the toggle so the user
  // can flip it; inline layouts default to hidden with no toggle visible.
  const [hideLegacy, setHideLegacy] = useState(true);
  const legacyToggleVisible = showLegacyToggle ?? variant === 'stacked';

  // When `includeImageProviders` is set on an LLM picker, surface the provider
  // list as labelled groups (Chat models / Image generation). Otherwise a
  // single flat group of the kind's own providers.
  const providerGroups = useMemo<{ label: string | null; items: ProviderCatalogEntry[] }[]>(() => {
    if (!catalog.data) return [];
    if (kind === 'llm' && includeImageProviders) {
      return [
        { label: 'Chat models', items: catalog.data.llm },
        { label: 'Image generation', items: catalog.data.image },
      ];
    }
    return [{ label: null, items: kind === 'llm' ? catalog.data.llm : catalog.data.image }];
  }, [catalog.data, kind, includeImageProviders]);

  const providers = useMemo<ProviderCatalogEntry[]>(
    () => providerGroups.flatMap(g => g.items),
    [providerGroups],
  );

  const activeEntry = useMemo(
    () => providers.find(p => p.key === provider) ?? null,
    [providers, provider],
  );

  // Model options: for LLM-Ollama merge the live daemon list + curated catalog
  // (via existing helper); for everything else apply the legacy filter + sort
  // newest-first against the entry's BE-declared AvailableModels.
  const modelOptions = useMemo<ModelOption[]>(() => {
    if (!activeEntry) return [];
    if (kind === 'llm' && activeEntry.key === 'ollama') {
      return ollamaModelOptions(ollamaInstalled.data?.models ?? []);
    }
    let filtered = filteredSortedModels(activeEntry.models, { hideLegacy });
    // 2026-05-26 — when the active op consumes a source image, hide t2i-only
    // models so the user can't repeat the "Transform with nano-banana-2 →
    // silent text-to-image" bug. We KEEP models whose tags include any
    // image-aware token (i2i / edit / inpaint / …) OR models with no tags
    // at all (defer to BE for those — let downstream decide).
    if (requiresImageAware) {
      filtered = filtered.filter(m => isImageAwareTagged(m.tags));
    }
    // Inverse gate for from-scratch generate (TextToImage): drop edit/inpaint/
    // upscale/etc. models that require a source image.
    if (textToImageOnly) {
      filtered = filtered.filter(m => !modelRequiresSourceImage(m));
    }
    return filtered.map(m => providerModelToOption(m));
  }, [activeEntry, kind, ollamaInstalled.data, hideLegacy, requiresImageAware, textToImageOnly]);

  // For the Autocomplete grouping callback we need the raw model lookup
  // (the ModelOption discards releaseDate + tags).
  const modelLookup = useMemo<Map<string, ProviderCatalogModel>>(() => {
    if (!activeEntry) return new Map();
    return new Map(activeEntry.models.map(m => [m.id, m]));
  }, [activeEntry]);

  // 2026-05-26 — snap the displayed value when the current model is a KNOWN
  // catalog entry that doesn't pass the requiresImageAware filter. Without
  // this, the dropdown lists only compatible options but the textbox keeps
  // showing the stale incompatible model id from before the op was switched.
  // FreeSolo / unknown ids are left alone (BE enforcement gates those).
  useEffect(() => {
    if (!requiresImageAware) return;
    if (!model || !activeEntry || modelOptions.length === 0) return;
    const known = activeEntry.models.find(m => m.id === model);
    if (!known) return; // freeSolo / unknown — defer to BE
    if (isImageAwareTagged(known.tags)) return; // already compatible
    const first = modelOptions[0]?.value;
    if (first && first !== model) onModelChange(first);
  }, [requiresImageAware, model, activeEntry, modelOptions, onModelChange]);

  // Mirror of the above for TextToImage: if the selected model requires a source
  // image (a stale edit-model pick), snap to the first text-to-image model so a
  // Generate never sends an edit model that 422s on missing image_urls.
  useEffect(() => {
    if (!textToImageOnly) return;
    if (!model || !activeEntry || modelOptions.length === 0) return;
    const known = activeEntry.models.find(m => m.id === model);
    if (!known) return; // freeSolo / unknown — defer to BE
    if (!modelRequiresSourceImage(known)) return; // already a t2i model
    const first = modelOptions[0]?.value;
    if (first && first !== model) onModelChange(first);
  }, [textToImageOnly, model, activeEntry, modelOptions, onModelChange]);

  const providerSelector =
    hideProviderWhenSingle && providers.length <= 1 ? null : (
      <Select
        size={size}
        value={providers.some(p => p.key === provider) ? provider : ''}
        onChange={e => onProviderChange(e.target.value as string)}
        displayEmpty
        disabled={disabled || catalog.isLoading}
        variant={variant === 'inline' ? 'standard' : 'outlined'}
        // `disableUnderline` is only a valid prop on Input/StandardInput, not on
        // Select itself — passing it here leaks the unknown attribute to the
        // rendered DOM and emits a React warning. Use sx to hide the standard-
        // variant underline directly.
        sx={
          variant === 'inline'
            ? {
                minWidth: 140,
                fontSize: '0.8125rem',
                '&:before, &:after, &:hover:not(.Mui-disabled):before': {
                  borderBottom: 'none',
                },
              }
            : { minWidth: 160 }
        }
        renderValue={selected => {
          if (catalog.isLoading) return 'Loading…';
          if (!selected) return 'Select provider';
          const entry = providers.find(p => p.key === selected);
          return entry?.name ?? selected;
        }}
      >
        {providerGroups.flatMap(group => {
          const items = group.items.map(p => (
            <MenuItem key={p.key} value={p.key} disabled={!p.available}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>{p.name}</span>
                {!p.available && (
                  <Typography component="span" variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
                    · misconfigured
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          ));
          // Only render a subheader when the list is actually grouped (more
          // than one group). A single flat group stays headerless as before.
          return group.label && providerGroups.length > 1
            ? [
                <ListSubheader key={`group-${group.label}`} disableSticky>
                  {group.label}
                </ListSubheader>,
                ...items,
              ]
            : items;
        })}
      </Select>
    );

  const modelSelector = (
    <Autocomplete
      size={size}
      freeSolo
      value={model}
      onChange={(_, v) => onModelChange(typeof v === 'string' ? v : (v?.value ?? ''))}
      onInputChange={(_, v) => onModelChange(v)}
      options={modelOptions}
      getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.label)}
      // Defense-in-depth against the "duplicate `FLUX.1 Kontext [pro]` key"
      // warning filed at `qa/bugs/2026-05-25-provider-catalog-duplicate-model-key.md`.
      // The BE catalog can legitimately surface two models with the same display
      // label (e.g. across families / providers); React still needs unique keys.
      // Use the model id (`option.value`) plus the active provider so collisions
      // are impossible.
      getOptionKey={opt =>
        typeof opt === 'string' ? `freeSolo:${opt}` : `${provider}:${opt.value}`
      }
      isOptionEqualToValue={(opt, val) =>
        typeof val === 'string' ? opt.value === val : opt.value === val.value
      }
      groupBy={opt => {
        if (typeof opt === 'string') return 'Custom';
        // For LLM-Ollama we keep the existing source-derived buckets
        // (Installed / Cloud / Suggested); for everything else use the
        // BE-tag-driven release-year grouping.
        if (kind === 'llm' && activeEntry?.key === 'ollama') return groupLabelFor(opt.source);
        const m = modelLookup.get(opt.value);
        return m ? groupLabelForModel(m) : 'Other';
      }}
      disabled={disabled || !activeEntry}
      // Show what each model costs at the point of choice. Without this the
      // only cost signal is `usage.estimatedCost` on the response — i.e. after
      // the user has already paid for the generation.
      renderOption={(props, opt) => {
        const { key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
        const label = typeof opt === 'string' ? opt : opt.label;
        const price = typeof opt === 'string' ? undefined : opt.price;
        const detail = typeof opt === 'string' ? undefined : opt.priceDetail;
        return (
          <Box
            component="li"
            key={key}
            {...liProps}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
            </Typography>
            {price && (
              <Tooltip title={detail ?? ''} placement="left">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                >
                  {price}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      }}
      sx={variant === 'inline' ? { minWidth: 240 } : { minWidth: 200, flex: 1 }}
      renderInput={params => (
        <TextField
          {...params}
          variant={variant === 'inline' ? 'standard' : 'outlined'}
          placeholder={!activeEntry ? 'Pick a provider first' : 'Model id'}
          InputProps={{
            ...params.InputProps,
            ...(variant === 'inline' ? { disableUnderline: true } : {}),
            endAdornment: (
              <>
                {catalog.isLoading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            style: variant === 'inline' ? { fontSize: '0.8125rem' } : undefined,
          }}
          label={variant === 'stacked' ? 'Model' : undefined}
        />
      )}
    />
  );

  const wrapperSx =
    variant === 'inline'
      ? { display: 'flex', flexDirection: 'row' as const, gap: 1, alignItems: 'center' }
      : { display: 'flex', flexDirection: 'row' as const, gap: 1, alignItems: 'flex-end' };

  const errorTooltip = activeEntry?.available === false ? (
    <Tooltip title={activeEntry.error ?? 'Provider unavailable'}>
      <Typography variant="caption" color="error.main" sx={{ whiteSpace: 'nowrap' }}>
        misconfigured
      </Typography>
    </Tooltip>
  ) : null;

  const ollamaFusionActive = kind === 'llm' && activeEntry?.key === 'ollama';
  const legacyCount = useMemo(
    () => (activeEntry?.models ?? []).filter(m => (m.tags ?? []).some(t => t === 'legacy' || t === 'deprecated')).length,
    [activeEntry],
  );
  const showLegacyToggleNow = legacyToggleVisible && !ollamaFusionActive && legacyCount > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Stack sx={wrapperSx}>
        {providerSelector}
        {modelSelector}
        {errorTooltip}
      </Stack>
      {showLegacyToggleNow && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideLegacy}
              onChange={e => setHideLegacy(e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Hide legacy models ({legacyCount})
            </Typography>
          }
          sx={{ ml: 0.25, mt: -0.25 }}
        />
      )}
    </Box>
  );
};

function providerModelToOption(m: ProviderCatalogModel): ModelOption {
  return {
    value: m.id,
    label: m.displayName || m.id,
    source: 'catalog',
    price: formatModelPrice(m.pricing),
    priceDetail: describeModelPrice(m.pricing),
  };
}

/**
 * Predicate for the `requiresImageAware` filter. Returns true when the model's
 * tags include any token that indicates it consumes / transforms a source
 * image. Untagged models pass through — we defer to the BE's capability check
 * (`ModelCapabilityChecker.EnforceCompatibility`) for those.
 *
 * Mirror of the BE's `IsModelImageAware` token set in
 * `ssgp-v3-agents-simple/Services/Images/ModelCapabilityChecker.cs` — keep them
 * in sync when adding new ops or model tags.
 */
/** Tags that mark a model as needing a source image (can't text-to-image). */
const SOURCE_REQUIRED_TAGS = new Set(['i2i', 'edit', 'inpaint', 'outpaint', 'upscale', 'face', 'tryon']);
/**
 * Model-id fragments for source-requiring variants that the BE catalog leaves
 * untagged (e.g. `flux-dev-image-to-image`, `flux-2-klein/inpaint`,
 * `flux-pro-kontext`, `…/redux`, `…/controlnet`, `birefnet`, `aura-sr`).
 */
const SOURCE_REQUIRED_ID =
  /\/edit\b|-edit\b|\/inpaint|inpainting|image-to-image|img2img|\/tryon|tryon|face-?swap|upscal|controlnet|redux|kontext|birefnet|reframe|outpaint|aura-sr/i;

/**
 * True when a model needs a source image and so cannot be used for a pure
 * text-to-image Generate. An explicit `t2i` tag always wins (keep it); otherwise
 * a source-ish tag or id fragment excludes it. Untagged plain-generate models
 * (flux-schnell, flux-dev, sdxl, …) fall through as text-to-image capable.
 */
export function modelRequiresSourceImage(m: ProviderCatalogModel): boolean {
  const tags = (m.tags ?? []).map(t => t.toLowerCase());
  if (tags.includes('t2i')) return false;
  if (tags.some(t => SOURCE_REQUIRED_TAGS.has(t))) return true;
  // Check the display name too — some BE ids are clean (`qwen-image`,
  // `flux-general`) while the label says "Image-to-Image" / "with ControlNet".
  return SOURCE_REQUIRED_ID.test(`${m.id} ${m.displayName ?? ''}`);
}

export function isImageAwareTagged(tags: readonly string[] | undefined): boolean {
  if (!tags || tags.length === 0) return true; // unknown → keep, BE will gate
  const aware = new Set(['i2i', 'edit', 'inpaint', 'outpaint', 'upscale', 'face', 'tryon']);
  // Any image-aware token wins — keep the model regardless of other tags.
  if (tags.some(t => aware.has(t.toLowerCase()))) return true;
  // No image-aware token. If the model explicitly declared itself "t2i",
  // filter it out. Otherwise (only meta tokens like "newest" / "legacy"),
  // keep and defer to the BE's capability check.
  if (tags.some(t => t.toLowerCase() === 't2i')) return false;
  return true;
}

function groupLabelFor(source: ModelOption['source']): string {
  switch (source) {
    case 'installed':
      return 'Installed locally';
    case 'cloud-tag':
      return 'Ollama Cloud';
    case 'catalog':
    default:
      return 'Available';
  }
}

/**
 * Pick a sensible default `{provider, model}` from the catalog. Used by
 * consumers that need to seed initial state before the user opens the
 * picker. Returns `{provider: '', model: ''}` when the catalog is empty.
 *
 * Strategy: prefer the requested preferred provider if it's available;
 * else first available provider; first model from that provider.
 */
export function defaultProviderAndModel(
  entries: ProviderCatalogEntry[],
  preferredProvider?: string,
): { provider: string; model: string } {
  const available = entries.filter(e => e.available);
  if (available.length === 0) return { provider: '', model: '' };
  const preferred =
    (preferredProvider && available.find(e => e.key === preferredProvider)) ||
    available[0];
  const firstModel = preferred.models[0]?.id ?? '';
  return { provider: preferred.key, model: firstModel };
}

/**
 * True when `key` identifies an image-generation provider in the catalog
 * (e.g. `"fal.ai"`). Used by the agent bar to decide whether a sent message
 * should route to image generation instead of LLM chat.
 */
export function isImageProviderKey(
  catalog: ProvidersCatalogResponse | undefined,
  key: string | undefined,
): boolean {
  if (!catalog || !key) return false;
  return catalog.image.some(e => e.key === key);
}
